import { useSignal } from "@preact/signals";
import { useCallback, useEffect } from "preact/hooks";
import { lt, t } from "@/i18n";
import { accuracyOf, topicsForLevel, type Tier } from "@/math";
import { calculatorOpen, hero, lang, settings } from "@/state/store";
import {
  answerTraining,
  backToSetup,
  closeTraining,
  hintTraining,
  nextTraining,
  startTraining,
  training,
  type TrainingTier,
} from "@/training/TrainingController";
import { ExplanationView, QuestionView } from "./QuestionView";
import { useKeyNav } from "./useKeyNav";

const TIERS: TrainingTier[] = ["auto", 1, 2, 3];

export function TrainingPanel() {
  const s = training.value;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const cur = training.value;
      if (!cur) return;
      const inInput = e.target instanceof HTMLInputElement;
      const key = e.key.toLowerCase();
      if (cur.phase === "question" && (key === "h" || key === "k")) {
        e.preventDefault();
        if (key === "h") hintTraining();
        else if (settings.value.calculator) calculatorOpen.value = !calculatorOpen.value;
        return;
      }
      if (inInput) return;
      if (cur.phase === "feedback" && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        nextTraining();
      } else if (e.key === "Escape") {
        if (calculatorOpen.value) calculatorOpen.value = false;
        else if (cur.phase === "setup") closeTraining();
        else backToSetup();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!s) return null;
  const L = lang.value;
  const hintUsed = s.eliminated.length > 0 || s.hintText !== null;
  return (
    <div class="training">
      <div class="training-top">
        <span class="title">🧙 {t("training.title")}</span>
        {s.phase !== "setup" && (
          <span class="score">
            {t("training.score", { correct: s.correct, total: s.answered })}
            {s.streak > 1 && <> · 🔥 {s.streak}</>}
          </span>
        )}
        <button class="close" onClick={() => (s.phase === "setup" ? closeTraining() : backToSetup())}>
          {s.phase === "setup" ? t("training.close") : t("training.stop")}
        </button>
      </div>
      {s.phase === "setup" && <Setup />}
      {s.phase === "question" && s.question && (
        <QuestionView
          question={s.question}
          onAnswer={answerTraining}
          eliminated={s.eliminated}
          hint={s.hintText ? s.hintText[L] : undefined}
        >
          <button class="hint" disabled={hintUsed} onClick={() => hintTraining()} title="H">
            💡 {t("training.hint")}
          </button>
          {settings.value.calculator && (
            <button onClick={() => (calculatorOpen.value = !calculatorOpen.value)} title="K">
              🧮 {t("battle.calculator")}
            </button>
          )}
        </QuestionView>
      )}
      {s.phase === "feedback" && s.feedback && <TrainingFeedback />}
    </div>
  );
}

function Setup() {
  const L = lang.value;
  const h = hero.value;
  const topic = useSignal<string>("mixed");
  const tier = useSignal<TrainingTier>("auto");
  const topics = topicsForLevel(h.level);
  const ids = ["mixed", ...topics.map((td) => td.id)];

  const activate = useCallback(
    (i: number) => {
      const id = ids[i];
      if (!id) return;
      topic.value = id;
      startTraining(id, tier.value);
    },
    [ids.join(","), tier.value],
  );
  const selected = useKeyNav({ count: ids.length, columns: 3, onActivate: activate, digits: false });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "+" || e.key === "=" || e.key === "PageUp")
        tier.value = TIERS[(TIERS.indexOf(tier.value) + 1) % TIERS.length] as TrainingTier;
      else if (e.key === "-" || e.key === "PageDown")
        tier.value = TIERS[(TIERS.indexOf(tier.value) + TIERS.length - 1) % TIERS.length] as TrainingTier;
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div class="training-setup">
      <p class="intro">{t("training.intro")}</p>
      <div class="topic-list">
        {ids.map((id, i) => {
          const isMixed = id === "mixed";
          const td = topics.find((x) => x.id === id);
          const stat = h.stats[id];
          const pct = stat && stat.attempts > 0 ? Math.round((stat.correct / stat.attempts) * 100) : null;
          const weak = stat && stat.attempts >= 5 && accuracyOf(stat) < 0.6;
          return (
            <button
              key={id}
              class={`${topic.value === id ? "on" : ""} ${selected.value === i ? "selected" : ""}`}
              onMouseEnter={() => (selected.value = i)}
              onClick={() => {
                topic.value = id;
                selected.value = i;
              }}
            >
              {isMixed ? t("training.mixed") : td ? lt(td.name) : id}
              {pct !== null && !isMixed && <span class={`acc ${weak ? "weak" : ""}`}>{pct}%</span>}
            </button>
          );
        })}
      </div>
      <div class="tier-row">
        <span>{t("training.difficulty")}</span>
        <div class="segmented">
          {TIERS.map((tv) => (
            <button key={String(tv)} class={tier.value === tv ? "on" : ""} onClick={() => (tier.value = tv)}>
              {tv === "auto" ? t("training.tier.auto") : t(`training.tier.${tv as Tier}`)}
            </button>
          ))}
        </div>
      </div>
      <div class="actions">
        <button class="primary" onClick={() => startTraining(ids[selected.value] ?? topic.value, tier.value)}>
          {t("training.start")} ⏎
        </button>
      </div>
      <p class="hint">
        {t("training.keys")} ·{" "}
        {L === "pl"
          ? "Bez limitu czasu, bez utraty zdrowia. +1 PD za każdą poprawną odpowiedź."
          : "No timer, no health loss. +1 XP for every correct answer."}
      </p>
    </div>
  );
}

function TrainingFeedback() {
  const s = training.value!;
  const f = s.feedback!;
  const L = lang.value;
  return (
    <div class={`feedback ${f.correct ? "ok" : "bad"}`}>
      <div class="headline">{f.correct ? t("training.correct") : t("battle.wrong")}</div>
      {!f.correct && (
        <ExplanationView correctAnswerText={f.correctAnswerText[L]} explanation={f.explanation[L]} />
      )}
      <button class="primary" onClick={() => nextTraining()}>
        {t("training.next")} ⏎
      </button>
    </div>
  );
}
